import {
  AddressLocationType,
  CreateAccountAddressInput,
  enumAccountType,
  enumAddressLocationType,
  enumAddressType,
  UpdateAccountAddressInput,
} from '@rematter/frontend-lib-api-client';
import { $selectedAddressId } from '@rematter/modules/crm/_state/accountDetails.state';
import { ensureValue, isDefined, isEqualBy } from '@rematter/ui-web';
import { atom, Getter } from 'jotai';

import { createAccountMutationAtom } from '../_graphql/mutationAccountCreate';
import { deleteAccountMutationAtom } from '../_graphql/mutationAccountDelete';
import { updateAccountMutationAtom } from '../_graphql/mutationAccountUpdate';
import { createAddressMutationAtom } from '../_graphql/mutationAddressCreate';
import { deleteAddressMutationAtom } from '../_graphql/mutationAddressDelete';
import { createContactMutationAtom } from '../_graphql/mutationContactCreate';
import { deleteContactMutationAtom } from '../_graphql/mutationContactDelete';
import { updateContactMutationAtom } from '../_graphql/mutationContactUpdate';
import { AddressRecord, ContactRecord } from '../_graphql/queryAccounts';

import { AccountFormState, AddressFormState, ContactFormState } from './_types';
import * as accountDetailsState from './accountDetails.state';
import * as accountListState from './accountList.state';

// 🔘🔘🔘 PLAIN ATOMS
const $rawAccountForm = atom<AccountFormState>({ accountManagerIds: [] });
const $rawContactForm = atom<ContactFormState>({});
const $rawAddressForm = atom<AddressFormState>({
  address: {
    addressType: enumAddressType.Standard,
  },
});

// 🟢🟢🟢 COMPUTED DATA -> contact form
export const $contactForm = atom(
  (get) => get($rawContactForm),
  (_get, set, update: Partial<ContactFormState>) => {
    set($rawContactForm, (prev) => ({
      ...prev,
      ...update,
    }));
  },
);

// 🟢🟢🟢 COMPUTED DATA -> account form
export const $accountForm = atom(
  (get) => get($rawAccountForm),
  (get, set, update: Partial<AccountFormState>) => {
    const accountForm = get($rawAccountForm);
    set($rawAccountForm, {
      ...accountForm,
      ...update,
    });
  },
);

// 🟢🟢🟢 COMPUTED DATA -> address form
export const $addressForm = atom(
  (get) => get($rawAddressForm),
  (get, set, update: Partial<AddressFormState>) => {
    const addressForm = get($rawAddressForm);
    set($rawAddressForm, {
      ...addressForm,
      ...update,
      address: {
        ...addressForm.address,
        ...(update.address || {}),
        coordinates: {
          latitude:
            update.address?.coordinates?.latitude ??
            addressForm.address.coordinates?.latitude ??
            0,
          longitude:
            update.address?.coordinates?.longitude ??
            addressForm.address.coordinates?.longitude ??
            0,
        },
      },
    });
  },
);

// 🟢🟢🟢 COMPUTED DATA -> contact form validation
export const $canSubmitContactForm = atom((get) => {
  const contactForm = get($contactForm);
  return !!(
    contactForm.firstName &&
    contactForm.lastName &&
    contactForm.email &&
    contactForm.phone
  );
});

// 🟢🟢🟢 COMPUTED DATA -> address form validation
export const $canSubmitAddressForm = atom((get) => {
  const addressForm = get($addressForm);
  const { address } = addressForm;

  return !!(
    address.streetAddress &&
    address.city &&
    address.state &&
    address.country &&
    address.postalCode
  );
});

// 🟢🟢🟢 COMPUTED DATA -> account form validation (for creation)
export const $canSubmitAccountForm = atom((get) => {
  const accountForm = get($accountForm);
  const canSubmitAddress = get($canSubmitAddressForm);

  const { name, type, relationship, email, website } = accountForm;

  // Required fields validation
  if (!name?.trim()) return false;
  if (!type) return false;
  if (
    !relationship?.isCustomer &&
    !relationship?.isSupplier &&
    !relationship?.isCarrier
  )
    return false;

  // Address validation (reuse existing address form validation)
  if (!canSubmitAddress) return false;

  // Email validation if provided
  if (email && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
    return false;
  }

  // Website validation if provided
  if (website && !website.match(/^https?:\/\/.+/)) {
    return false;
  }

  return true;
});

// 🟢🟢🟢 COMPUTED DATA -> has unsaved changes
export const $hasUnsavedAccountChanges = atom((get) => {
  const accountForm = get($accountForm);
  const addressForm = get($addressForm);

  return (
    !isEqualBy(
      accountForm,
      accountFormDefaults,
      (a) => a.name,
      (a) => a.type,
      (a) => a.email,
      (a) => a.phone,
      (a) => a.website,
      (a) => JSON.stringify([...a.accountManagerIds].sort()),
      (a) => a.relationship?.isCustomer,
      (a) => a.relationship?.isSupplier,
      (a) => a.notes,
    ) ||
    !isEqualBy(
      addressForm,
      addressFormDefaults,
      (a) => a?.name,
      (a) => a?.address.addressType,
      (a) => a?.address.streetAddress,
      (a) => a?.address.city,
      (a) => a?.address.state,
      (a) => a?.address.country,
      (a) => a?.address.postalCode,
    )
  );
});

// 🟢🟢🟢 COMPUTED DATA -> has unsaved address changes
//        THIS ONE IS ONLY USED FOR NEW ADDRESSES
export const $hasUnsavedAddressChanges = atom((get) => {
  const addressForm = get($addressForm);

  return !isEqualBy(
    addressForm,
    addressFormDefaults,
    (a) => a.name,
    (a) => a.address.addressType,
    (a) => a.address.city,
    (a) => a.address.state,
    (a) => a.address.country,
    (a) => a.address.postalCode,
    (a) => a.address.streetAddress,
  );
});

// 🟢🟢🟢 COMPUTED DATA -> has unsaved contact changes
export const $hasUnsavedContactChanges = atom((get) => {
  const contactForm = get($contactForm);
  const contactFormDefaults: ContactFormState = {};

  const isEditing = !!contactForm.id;

  if (isEditing) {
    const originalContact = get(
      accountDetailsState.$selectedAddress,
    )?.contacts.find((c) => c.id === contactForm.id);

    if (!originalContact) {
      return false;
    }

    return !isEqualBy(
      contactForm,
      originalContact,
      (a) => a.firstName,
      (a) => a.lastName,
      (a) => a.email,
      (a) => a.phone,
      (a) => a.role,
      (a) => a.department,
      (a) => a.notes,
      (a) => a.isPrimary,
    );
  }

  return !isEqualBy(
    contactForm,
    contactFormDefaults,
    (a) => a.firstName,
    (a) => a.lastName,
    (a) => a.email,
    (a) => a.phone,
    (a) => a.role,
    (a) => a.department,
    (a) => a.notes,
    (a) => a.isPrimary,
  );
});

// 🟢🟢🟢 COMPUTED DATA -> primary checkbox state
export const $shouldDisablePrimaryCheckbox = atom((get) => {
  const contactForm = get($contactForm);
  const account = get(accountDetailsState.$selectedAccount);

  return !!(
    contactForm.id &&
    contactForm.isPrimary &&
    account?.addresses?.length === 1
  );
});

// 🟢🟢🟢 COMPUTED DATA -> account editing permission
export const $canEditAccount = atom(
  () =>
    // TODO: replace by actual editing permission
    true,
);

// 🟡🟡🟡 HIDDEN HELPER -> refetch data
const refetchData = (get: Getter) => {
  get(accountDetailsState.$remoteAccount).refetch();
  get(accountListState.$accountList).refetch();
};

// 🔵🔵🔵 HELPER -> address record to address input
const addressRecordToAddressInput = (addressRecord: AddressRecord) => ({
  addressType: addressRecord.addressType,
  city: addressRecord.city,
  coordinates: {
    latitude: addressRecord.coordinates?.latitude ?? 0,
    longitude: addressRecord.coordinates?.longitude ?? 0,
  },
  country: addressRecord.country,
  isMailingAddress: addressRecord.isMailingAddress,
  postalCode: addressRecord.postalCode,
  state: addressRecord.state,
  streetAddress: addressRecord.streetAddress,
  streetAddress2: addressRecord.streetAddress2,
});

// 🔵🔵🔵 HELPER -> address form to create address input (for new addresses)
const addressFormToCreateAddressInput = (
  addressForm: AddressFormState,
  accountId: string,
): CreateAccountAddressInput & { locationType: AddressLocationType } => ({
  accountId,
  name: addressForm.name,
  locationType: addressForm.locationType ?? enumAddressLocationType.Shipping,
  customAttributes: addressForm.customAttributes,
  address: {
    addressType: addressForm.address.addressType ?? enumAddressType.Standard,
    city: addressForm.address.city || '',
    country: addressForm.address.country || '',
    isMailingAddress: addressForm.address.isMailingAddress || false,
    postalCode: addressForm.address.postalCode || '',
    state: addressForm.address.state || '',
    streetAddress: addressForm.address.streetAddress || '',
    streetAddress2: addressForm.address.streetAddress2 || undefined,
    coordinates: addressForm.address.coordinates
      ? {
          latitude: addressForm.address.coordinates.latitude,
          longitude: addressForm.address.coordinates.longitude,
        }
      : undefined,
  },
});

// 🔵🔵🔵 HELPER -> address form to update address input (for existing addresses)
const addressFormToUpdateAddressInput = (
  addressForm: AddressFormState,
): UpdateAccountAddressInput => ({
  id: addressForm.id,
  name: addressForm.name,
  customAttributes: addressForm.customAttributes,
  address: {
    addressType: addressForm.address.addressType ?? enumAddressType.Standard,
    city: addressForm.address.city || '',
    country: addressForm.address.country || '',
    isMailingAddress: addressForm.address.isMailingAddress || false,
    postalCode: addressForm.address.postalCode || '',
    state: addressForm.address.state || '',
    streetAddress: addressForm.address.streetAddress || '',
    streetAddress2: addressForm.address.streetAddress2 || undefined,
    coordinates: addressForm.address.coordinates
      ? {
          latitude: addressForm.address.coordinates.latitude,
          longitude: addressForm.address.coordinates.longitude,
        }
      : undefined,
  },
});

// 🔵🔵🔵 HELPER -> contact record to contact input (for passing existing contacts through)
const contactRecordToContactInput = (c: ContactRecord, accountId: string) => ({
  id: c.id,
  accountId,
  addressAs: c.addressAs,
  department: c.department,
  email: c.email,
  fullName: c.fullName,
  isPrimary: c.isPrimary,
  notes: c.notes,
  phone: c.phone,
  prefix: c.prefix,
  role: c.role,
});

const addressFormDefaults: AddressFormState = {
  name: '',
  address: {
    country: 'US',
    addressType: enumAddressType.Standard,
    isMailingAddress: true,
  },
};

const accountFormDefaults: AccountFormState = {
  accountManagerIds: [],
  type: enumAccountType.Commercial,
  relationship: {
    isCustomer: true,
    isSupplier: false,
    isCarrier: false,
  },
};

// 🟠🟠🟠 ACTION -> RESET account form
export const $resetAccountForm = atom(
  null,
  (_get, set, value?: Partial<AccountFormState>) => {
    set($rawAccountForm, {
      ...accountFormDefaults,
      ...value,
      accountManagerIds:
        value?.accountManagerIds ?? accountFormDefaults.accountManagerIds,
      relationship: {
        isCustomer:
          value?.relationship?.isCustomer ??
          accountFormDefaults.relationship?.isCustomer ??
          false,
        isSupplier:
          value?.relationship?.isSupplier ??
          accountFormDefaults.relationship?.isSupplier ??
          false,
        isCarrier:
          value?.relationship?.isCarrier ??
          accountFormDefaults.relationship?.isCarrier ??
          false,
      },
    });
  },
);

// 🟠🟠🟠 ACTION -> RESET contact form
export const $resetContactForm = atom(
  null,
  (_get, set, contact?: ContactRecord) => {
    set($rawContactForm, contact ?? {});
  },
);

// 🟠🟠🟠 ACTION -> RESET address form
export const $resetAddressForm = atom(
  null,
  (_get, set, address?: Partial<AddressRecord>) => {
    if (address) {
      set($rawAddressForm, {
        id: address.id,
        name: address.name,
        address: {
          addressType: address.addressType,
          city: address.city,
          country: address.country,
          isMailingAddress: address.isMailingAddress,
          postalCode: address.postalCode,
          state: address.state,
          streetAddress: address.streetAddress,
          coordinates: address.coordinates,
        },
      });
    } else {
      set($rawAddressForm, {
        name: '',
        address: {
          country: 'US',
          addressType: enumAddressType.Standard,
          isMailingAddress: true,
        },
      });
    }
  },
);

// 🟠🟠🟠 ACTION -> CREATE account
export const $createRemoteAccount = createAccountMutationAtom({
  onSuccess: refetchData,
});

// 🟠🟠🟠 ACTION -> CREATE account (with form data mapping)
export const $createAccount = atom(
  null,
  async (get, set): Promise<string | undefined> => {
    const accountForm = get($accountForm);
    const addressForm = get($addressForm);

    const accountData = {
      ...accountForm,
      name: accountForm.name || '',
      type: accountForm.type || enumAccountType.Commercial,
      accountManagerIds: accountForm.accountManagerIds,
      relationship: accountForm.relationship || {
        isCustomer: true,
        isSupplier: false,
        isCarrier: false,
      },
      addresses: [
        {
          name: addressForm.name,
          locationType:
            addressForm.locationType ?? enumAddressLocationType.Shipping,
          address: {
            streetAddress: addressForm.address.streetAddress || '',
            city: addressForm.address.city || '',
            state: addressForm.address.state || '',
            postalCode: addressForm.address.postalCode || '',
            country: addressForm.address.country || '',
            isMailingAddress: addressForm.address.isMailingAddress || false,
            addressType:
              addressForm.address.addressType || enumAddressType.Standard,
          },
          contacts: [],
        },
      ],
    };

    const result = await set($createRemoteAccount, accountData);

    set($resetAccountForm);
    set($resetAddressForm);

    return result?.crmAccountCreate?.id;
  },
);

// 🟠🟠🟠 ACTION -> UPDATE account
export const $updateRemoteAccount = updateAccountMutationAtom({
  onSuccess: refetchData,
});

// 🟠🟠🟠 ACTION -> DELETE account
export const $deleteRemoteAccount = deleteAccountMutationAtom({
  onSuccess: refetchData,
});

// 🟠🟠🟠 ACTION -> CREATE address
export const $createRemoteAddress = createAddressMutationAtom({
  onSuccess: refetchData,
});

// 🟠🟠🟠 ACTION -> DELETE address
export const $deleteRemoteAddress = deleteAddressMutationAtom({
  onSuccess: (get, set) => {
    set($selectedAddressId, undefined);
    refetchData(get);
  },
});

// 🟠🟠🟠 ACTION -> CREATE remote contact
const $createRemoteContact = createContactMutationAtom({
  onSuccess: refetchData,
});

// 🟠🟠🟠 ACTION -> UPDATE remote contact
const $updateRemoteContact = updateContactMutationAtom({
  onSuccess: refetchData,
});

// 🟠🟠🟠 ACTION -> DELETE remote contact
export const $deleteRemoteContact = deleteContactMutationAtom({
  onSuccess: refetchData,
});

// 🟠🟠🟠 ACTION -> CREATE contact
export const $createContact = atom(null, async (get, set) => {
  const selectedAccount = get(accountDetailsState.$selectedAccount);
  const selectedAddress = get(accountDetailsState.$selectedAddress);
  const contactForm = get($contactForm);

  // Make contact primary if it's the first one in the address
  const isFirstContactInAddress = selectedAddress?.contacts?.length === 0;
  const isPrimary = isFirstContactInAddress || !!contactForm.isPrimary;

  const fullName = [contactForm.firstName, contactForm.lastName]
    .filter(isDefined)
    .join(' ');

  await set($createRemoteContact, {
    accountId: ensureValue(selectedAccount?.id),
    addressId: selectedAddress?.id,
    fullName,
    addressAs: fullName,
    email: contactForm.email,
    phone: contactForm.phone,
    prefix: contactForm.prefix,
    role: contactForm.role,
    department: contactForm.department,
    notes: contactForm.notes,
    isPrimary,
  });

  set($resetContactForm);
});

// 🟠🟠🟠 ACTION -> UPDATE contact
export const $updateContact = atom(null, async (get, set) => {
  const contactForm = get($contactForm);

  const fullName = [contactForm.firstName, contactForm.lastName]
    .filter(isDefined)
    .join(' ');

  await set($updateRemoteContact, {
    id: ensureValue(contactForm.id),
    fullName,
    addressAs: fullName,
    email: contactForm.email,
    phone: contactForm.phone,
    prefix: contactForm.prefix,
    role: contactForm.role,
    department: contactForm.department,
    notes: contactForm.notes,
    isPrimary: contactForm.isPrimary,
  });

  set($resetContactForm);
});

// 🟠🟠🟠 ACTION -> DELETE contact
export const $deleteContact = atom(
  null,
  async (_get, set, contactId: string) => {
    await set($deleteRemoteContact, { id: contactId });
  },
);

// 🟠🟠🟠 ACTION -> CREATE account address
export const $createAccountAddress = atom(null, async (get, set) => {
  const addressForm = get($addressForm);
  const account = get(accountDetailsState.$selectedAccount);

  await set(
    $createRemoteAddress,
    addressFormToCreateAddressInput(addressForm, ensureValue(account?.id)),
  );
  set($resetAddressForm);
});

// 🟠🟠🟠 ACTION -> UPDATE account address
export const $updateAccountAddress = atom(null, async (get, set) => {
  const selectedAccount = get(accountDetailsState.$selectedAccount);
  const addressForm = get($addressForm);
  const accountId = ensureValue(selectedAccount?.id);

  const updatedAccount = {
    id: accountId,
    addresses: selectedAccount?.addresses?.map(
      (address): UpdateAccountAddressInput => {
        const isCurrentAddress = address.id === addressForm.id;

        if (isCurrentAddress) {
          return {
            ...addressFormToUpdateAddressInput(addressForm),
            id: address.id,
            contacts: address.contacts.map((c) =>
              contactRecordToContactInput(c, accountId),
            ),
          };
        }

        return {
          id: address.id,
          name: address.name,
          address: addressRecordToAddressInput(address),
          contacts: address.contacts.map((c) =>
            contactRecordToContactInput(c, accountId),
          ),
        };
      },
    ),
  };

  await set($updateRemoteAccount, updatedAccount);
  set($resetAddressForm);
});
